import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup, Input, Spinner } from 'reactstrap';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import intl from 'react-intl-universal';
import classNames from 'classnames';
import { v4 as uuid } from 'uuid';

import Icon from 'components/Icon';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import SegmentTabs from 'components/SegmentTabs';
import FormQuestion from 'modules/forms/components/FormQuestion';
import {
  EAppointmentFormStatus,
  EQuestionType,
  IFormSection,
  TQuestion,
  IHealthInfo,
  EFormSectionType,
  IUserSignaturePost,
} from 'modules/forms/interfaces/form.interfaces';
import {
  postAppointmentForm,
  setCurrentFormData,
  setCurrentFormStep,
} from 'modules/forms/actions/form.actions';
import toastEmit from 'helpers/toast.helpers';
import { ETypeToast } from 'interfaces/toast.interfaces';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { travelClinicForm } from '../constants/forms.constants';
import { getFormResultHTML } from '../helpers/formResult.helper';
import FormResult from './FormResult';
import { getFormStatus } from '../helpers/getFormStatus';
import { r } from '../constants/routes.constants';

const tabs = [
  {
    id: 'type',
    label: 'Type',
  },
  {
    id: 'draw',
    label: 'Draw',
  },
];

const FormStep = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currentForm = useTypedSelector(state => state.form.currentForm);
  const currentStep = useTypedSelector(state => state.form.currentStep);
  const appointment = useTypedSelector(state => state.form.appointment);
  const formUuid = useTypedSelector(state => state.form.formUuid);
  const guest = useTypedSelector(state => state.auth.guest);
  const profiles = useTypedSelector(state => state.auth.profiles);
  const postLoading = useTypedSelector(state => state.form.loading.postForm);
  const section = currentForm?.sections?.[currentStep];

  const signatureCanvasRef = useRef<SignatureCanvas>(null);
  const [activeTab, setActiveTab] = useState<IUserSignaturePost['signatureType']>(
    currentForm?.signature?.signatureType || 'type',
  );
  const [fullName, setFullName] = useState(currentForm?.signature?.signatureName || '');

  const patient = useMemo(() => {
    return appointment && profiles?.length
      ? profiles?.find(profile => profile.customerUuid === appointment.customerUuid)
      : null;
  }, [profiles, appointment]);

  const methods = useForm<Partial<IFormSection>>({
    defaultValues: {
      questions: section?.questions || [],
    },
    mode: 'onSubmit',
  });
  const { control, handleSubmit, watch, reset } = methods;
  const { fields: questions, append } = useFieldArray({
    control,
    name: 'questions',
  });

  const isRequired = !!watch('questions')?.some(
    q => q.required && (q.value === '' || q.value === undefined) && !q.values?.length,
  );

  const healthMeasurementQuestion = useMemo(() => {
    return questions?.find(q => q.type === EQuestionType.HEALTH_MEASUREMENT && q.required);
  }, [questions]);

  const healthMeasurementValue = healthMeasurementQuestion
    ? (watch(`questions.${questions.indexOf(healthMeasurementQuestion)}.value`) as IHealthInfo)
    : null;

  const isHealthMeasurementValid = useMemo(() => {
    if (!healthMeasurementQuestion) return true;

    return healthMeasurementValue?.weight && healthMeasurementValue?.height;
  }, [healthMeasurementQuestion, healthMeasurementValue]);

  const isSubmitDisabled = postLoading || !isHealthMeasurementValid || isRequired;

  const mainRef = useRef<HTMLDivElement>(null);
  const [isScrolledFromTop, setScrolledFromTop] = useState(false);

  useEffect(() => {
    const mainEl = mainRef.current;

    const handleScroll = () => {
      if (mainEl) {
        const { scrollTop } = mainEl;

        const scrolledFromTop = scrollTop > 0;

        setScrolledFromTop(scrolledFromTop);
      }
    };

    setTimeout(() => {
      handleScroll();
    }, 50);
    mainEl?.addEventListener('scroll', handleScroll);

    return () => {
      mainEl?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    reset({ questions: currentForm?.sections?.[currentStep]?.questions });
  }, [currentStep, currentForm, reset]);

  const onBack = () => {
    history.goBack();
    dispatch(setCurrentFormStep(currentStep - 1));
  };

  useEffect(() => {
    const generateSignatureFromText = (name: string) => {
      if (!signatureCanvasRef.current) return;

      signatureCanvasRef.current.clear();

      const canvas = signatureCanvasRef.current.getCanvas();
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.font = "36px 'Brush Script MT', cursive";
      ctx.fillStyle = 'black';

      const textWidth = ctx.measureText(name).width;
      const x = (canvas.width - textWidth) / 2;
      const y = canvas.height / 2 + 10;

      ctx.fillText(name, x, y);
    };

    if (activeTab === 'type' && fullName.trim()) {
      generateSignatureFromText(fullName);
    }
  }, [fullName, activeTab, signatureCanvasRef]);

  useEffect(() => {
    if (activeTab === 'draw' && signatureCanvasRef.current) {
      signatureCanvasRef.current.clear();
    }
  }, [activeTab, signatureCanvasRef]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value);
  };

  const handleClear = () => {
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.clear();
    }
  };

  const onSubmit = (values: Partial<IFormSection>) => {
    if (
      section?.type === EFormSectionType.SIGNATURE &&
      (!fullName?.trim()?.length || (activeTab === 'draw' && signatureCanvasRef.current?.isEmpty()))
    ) {
      toastEmit({
        type: ETypeToast.error,
        title: 'Please provide your name and signature to continue',
      });
      return;
    }

    if (section?.type === EFormSectionType.SIGNATURE) {
      const canvas = signatureCanvasRef.current?.getTrimmedCanvas();
      canvas?.toBlob(blob => {
        if (blob) {
          const signatureFile = new File([blob], 'signature.png', { type: 'image/png' });
          dispatch(
            setCurrentFormData({
              ...(currentForm || {}),
              signature: {
                signatureName: fullName,
                signatureFile,
                signatureBlob: blob,
                signatureType: activeTab,
              },
            }),
          );
        }
      }, 'image/png');
    }

    const newSections = currentForm?.sections?.map(item =>
      item.uuid === section?.uuid ? { ...item, questions: values.questions || [] } : item,
    );
    dispatch(setCurrentFormData({ ...(currentForm || {}), sections: newSections }));
    const nextStep = currentStep + 1;

    const currentQuestions = values.questions || [];
    const disqualifyingQuestion = currentQuestions.find(
      (question, index) =>
        question.disqualification &&
        question.disqualification.value !== undefined &&
        watch(`questions.${index}.value`) === question.disqualification.value,
    );

    if (disqualifyingQuestion) {
      const newForm = { ...currentForm, uuid: formUuid, sections: newSections };
      dispatch(
        postAppointmentForm({
          form: {
            appointmentUuid: appointment?.uuid,
            status: EAppointmentFormStatus.TO_GP,
            agreedTerms: true,
            response: getFormResultHTML(newForm, FormResult),
          },
          route: r.formDisqualification,
        }),
      );
      return;
    }

    if (
      newSections?.length &&
      nextStep === newSections.length - 1 &&
      !guest &&
      !currentForm?.showPatientConsent
    ) {
      const newForm = { ...currentForm, uuid: formUuid, sections: newSections };
      const formStatus = getFormStatus(newForm, patient, guest);
      dispatch(
        postAppointmentForm({
          form: {
            appointmentUuid: appointment?.uuid,
            status: formStatus,
            agreedTerms: true,
            response: getFormResultHTML(newForm, FormResult),
          },
          route: `/form/${formUuid}/${newSections?.[nextStep]?.uuid}`,
          onSuccess: () => dispatch(setCurrentFormStep(nextStep)),
        }),
      );
      return;
    }

    history.push({
      pathname: `/form/${formUuid}/${newSections?.[nextStep]?.uuid}`,
      search: history.location.search,
    });
    dispatch(setCurrentFormStep(nextStep));
  };

  return (
    <Page>
      <PageHeader
        step={currentStep + 1}
        maxSteps={currentForm?.sections?.length}
        isScrolledFromTop={isScrolledFromTop}
      />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main ref={mainRef} id="page-main-scrollable" className="page-main">
              {((questions?.length !== 1 && section?.title) ||
                !!section?.description ||
                !!section?.showTitle) && (
                <div className="form-header">
                  {(questions?.length !== 1 || !!section.showTitle) && <h3>{section?.title}</h3>}
                  {!!section?.description && <div>{section?.description}</div>}
                </div>
              )}
              {section?.type === EFormSectionType.SIGNATURE && (
                <div className="form-signature">
                  <div>
                    <p style={{ maxWidth: '800px', marginBottom: '24px' }}>
                      I confirm that the information I have provided above is accurate and complete
                      to the best of my knowledge. I understand that this information will be used
                      to plan my consultation and any recommended treatment.
                    </p>

                    <h4 style={{ marginBottom: '16px' }}>Signature</h4>
                    <div style={{ maxWidth: '344px' }}>
                      <div style={{ position: 'relative' }}>
                        <FormGroup className="form-floating-label" style={{ marginBottom: '16px' }}>
                          <div className="mb-sm">Full Name</div>
                          <Input
                            type="text"
                            bsSize="lg"
                            value={fullName}
                            placeholder="Enter your name"
                            onChange={handleNameChange}
                          />
                        </FormGroup>
                        <p className="text-gray-500" style={{ marginBottom: '16px' }}>
                          Draw your signature in the box below (mouse or touch), or use the
                          auto-generated version from your name.
                        </p>
                        <SegmentTabs
                          tabs={tabs}
                          activeTab={activeTab}
                          onTabChange={value =>
                            setActiveTab(value as IUserSignaturePost['signatureType'])
                          }
                        />
                        <SignatureCanvas
                          ref={signatureCanvasRef}
                          canvasProps={{
                            width: 340,
                            height: 200,
                            style: { borderRadius: '6px', border: '3px dotted #d1d5db' },
                          }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            style={{ alignSelf: 'flex-end' }}
                            className="flex items-center gap-sm"
                            color="icon"
                            outline
                            onClick={handleClear}
                          >
                            <Icon iconName="trash-undo" />
                            Clear
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!!questions?.length && (
                <div className={classNames('form-questions', section?.className)}>
                  {questions.map((question: TQuestion, index: number) => {
                    if (
                      question.conditions?.length &&
                      !question.conditions.every(condition => {
                        return (
                          watch(`questions.${condition.field}.value`) &&
                          condition.values.includes(
                            watch(`questions.${condition.field}.value`) as
                              | string
                              | boolean
                              | undefined,
                          )
                        );
                      })
                    ) {
                      return;
                    }

                    return (
                      <FormQuestion
                        key={question.uuid}
                        question={question}
                        sectionTitle={
                          questions?.length === 1
                            ? section?.title
                            : `${!section?.title ? question.title : ''}`
                        }
                        index={index}
                        isLast={index === questions.length - 1}
                      />
                    );
                  })}
                </div>
              )}

              <PageFooter className="page-footer--scrollable">
                {currentStep !== 0 && (
                  <Button
                    color="primary"
                    outline
                    type="button"
                    block
                    disabled={postLoading}
                    onClick={onBack}
                  >
                    <Icon iconName="arrow-left" />
                    {intl.get('button.back')}
                  </Button>
                )}
                {!!section?.button && (
                  <Button
                    color="primary"
                    outline
                    onClick={() => {
                      append([
                        { uuid: uuid(), type: EQuestionType.SEPARATOR },
                        ...(travelClinicForm.sections?.[0]?.questions || []).map(q => ({
                          ...q,
                          uuid: uuid(),
                        })),
                      ]);
                      setTimeout(() => {
                        if (mainRef.current) {
                          mainRef.current.scrollTop = mainRef.current.scrollHeight;
                        }
                      }, 0);
                    }}
                  >
                    {section.button}
                  </Button>
                )}
                <Button
                  color="primary"
                  type="button"
                  block
                  disabled={isSubmitDisabled}
                  onClick={handleSubmit(onSubmit)}
                >
                  {postLoading && <Spinner size="sm" />}
                  {intl.get('button.next')} <Icon iconName="arrow-right" />
                </Button>
              </PageFooter>
            </main>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default FormStep;
